import React from 'react';

/**
 * ContactSuggestion Component
 * Shows suggested contact match when creating a party
 * Allows user to accept or reject the suggestion
 */
const ContactSuggestion = ({ contact, onAccept, onReject, confidence }) => {
  if (!contact) return null;

  const getConfidenceColor = (conf) => {
    if (conf >= 90) return 'text-green-600 bg-green-50 border-green-200';
    if (conf >= 70) return 'text-blue-600 bg-blue-50 border-blue-200';
    return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  };

  const getConfidenceBadge = (conf) => {
    if (conf >= 90) return '✓ Exact Match';
    if (conf >= 70) return '~ Likely Match';
    return '? Possible Match';
  };

  const getConfidenceIcon = (conf) => {
    if (conf >= 90) return '🎯';
    if (conf >= 70) return '📱';
    return '❓';
  };

  return (
    <div className="bg-white border-2 border-blue-400 rounded-lg p-4 mb-4 shadow-lg animate-slideDown">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          <span className="text-3xl">{getConfidenceIcon(confidence)}</span>
          <div>
            <h4 className="text-lg font-semibold text-gray-900">
              Contact Found in Your Phone
            </h4>
            <span className={`inline-block text-xs px-2 py-1 rounded mt-1 ${getConfidenceColor(confidence)}`}>
              {getConfidenceBadge(confidence)}
            </span>
          </div>
        </div>
      </div>

      {/* Contact Details */}
      <div className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-600 font-medium">Name:</span>
            <p className="font-semibold text-gray-900 mt-1">{contact.name}</p>
          </div>
          
          {contact.phones && contact.phones.length > 0 && (
            <div>
              <span className="text-gray-600 font-medium">Phone:</span>
              <p className="font-semibold text-gray-900 mt-1">
                {contact.phones[0].number}
              </p>
            </div>
          )}
          
          {contact.emails && contact.emails.length > 0 && (
            <div>
              <span className="text-gray-600 font-medium">Email:</span>
              <p className="font-semibold text-gray-900 mt-1 text-xs">
                {contact.emails[0].address}
              </p>
            </div>
          )}
          
          {contact.organization && (
            <div>
              <span className="text-gray-600 font-medium">Company:</span>
              <p className="font-semibold text-gray-900 mt-1">{contact.organization}</p>
            </div>
          )}
        </div>
      </div>

      {/* Info Message */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
        <div className="flex items-start space-x-2">
          <span className="text-blue-600 mt-0.5">💡</span>
          <p className="text-sm text-blue-800">
            <strong className="font-semibold">Auto-fill: </strong>
            We'll fill in the contact details for you. You can edit them before saving.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={() => onAccept(contact)}
          className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
        >
          <span>✓</span>
          <span>Use This Contact</span>
        </button>
        <button
          onClick={onReject}
          className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
        >
          ✗ Ignore
        </button>
      </div>

      {/* Footer Note */}
      <p className="text-xs text-gray-500 mt-3 text-center">
        This contact is from your phone. Data is synced securely and stored locally.
      </p>
    </div>
  );
};

export default ContactSuggestion;

// CSS Animation (add to your global CSS or inline styles)
const styles = `
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slideDown {
  animation: slideDown 0.3s ease-out;
}
`;

// Inject styles if not already present
if (typeof document !== 'undefined') {
  const styleId = 'contact-suggestion-styles';
  if (!document.getElementById(styleId)) {
    const styleTag = document.createElement('style');
    styleTag.id = styleId;
    styleTag.innerHTML = styles;
    document.head.appendChild(styleTag);
  }
}











